import { useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";

/**
 * Modal es el diálogo en el que viven los formularios de alta y edición.
 *
 * Antes cada pantalla abría su formulario **encima** de la tabla, y eso tenía
 * dos problemas: en una lista larga el formulario empujaba la tabla fuera de
 * la vista, y al editar una fila no se veía qué fila se estaba editando porque
 * el formulario aparecía arriba, lejos de ella.
 *
 * No usa `<dialog>` nativo. Lo pensé, y lo descarté por una razón concreta: su
 * backdrop se pinta con `::backdrop`, que no hereda las variables CSS del tema
 * del portal, y `showModal()` hay que llamarlo desde un efecto en lugar de
 * derivarlo del estado de React —lo que abre la puerta a que el DOM y el
 * estado discrepen—. Lo que sí hace falta de un diálogo de verdad está aquí
 * abajo, explícito.
 */
export function Modal({
  title,
  description,
  onClose,
  children,
  width = "md",
}: {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** `lg` para formularios con editor de markup, que necesitan ancho. */
  width?: "md" | "lg";
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Se guarda quién tenía el foco para devolverlo al cerrar: si no, el foco
  // vuelve al principio del documento y quien navega con teclado pierde el
  // sitio.
  const devolverFoco = useRef<Element | null>(null);

  const enfocables = useCallback((): HTMLElement[] => {
    if (panel.current === null) return [];
    return [
      ...panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => el.offsetParent !== null);
  }, []);

  useEffect(() => {
    devolverFoco.current = document.activeElement;

    // El primer campo, no el panel: al abrir un alta se empieza a escribir.
    const primero = enfocables()[0];
    if (primero !== undefined) primero.focus();
    else panel.current?.focus();

    // El fondo no se desplaza mientras el diálogo está abierto. Se guarda el
    // valor anterior en lugar de poner "" al cerrar, porque la página podría
    // tener el suyo.
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      // Trampa de foco. Sin ella, tabular saca el foco del diálogo hacia la
      // página de detrás, que está tapada: el usuario mueve el foco a sitios
      // que no ve.
      const campos = enfocables();
      if (campos.length === 0) return;
      const primero = campos[0];
      const ultimo = campos[campos.length - 1];
      if (primero === undefined || ultimo === undefined) return;

      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = overflowAnterior;
      if (devolverFoco.current instanceof HTMLElement) devolverFoco.current.focus();
    };
  }, [onClose, enfocables]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-slate-950/80 p-4 backdrop-blur-sm sm:items-center sm:p-6"
      // El clic en el fondo cierra, pero solo si empezó y acabó en el fondo:
      // arrastrar para seleccionar texto dentro del diálogo y soltar fuera no
      // debe cerrar y perder lo escrito.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-modal"
        tabIndex={-1}
        className={`panel my-auto w-full ${
          width === "lg" ? "max-w-3xl" : "max-w-xl"
        } p-5 shadow-2xl shadow-black/50 outline-none`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="titulo-modal" className="text-base font-semibold text-slate-100">
              {title}
            </h2>
            {description !== undefined && (
              <div className="mt-1 text-xs text-slate-400">{description}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-mr-1 -mt-1 rounded-lg p-1.5 text-slate-500 transition hover:bg-white/5 hover:text-slate-200"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
